/** Everything that needs a look, worst first; acknowledge to say "seen, on it". */
import { createContext, useContext, useState, type ReactNode } from "react";
import { api, ApiError, type Alert, type Site } from "../api.ts";
import { ago, clock } from "../format.ts";
import { go } from "../route.ts";
import { can } from "../control.ts";
import { lazyScreen, Problem, Section, Spinner, useLoad, useNow } from "../ui.tsx";

// Set up and Who's told are big and opened now and then: loaded when first shown.
const AlertRules = lazyScreen(() => import("./alert-setup.tsx").then((m) => m.AlertRules));
const AlertPeople = lazyScreen(() => import("./alert-setup.tsx").then((m) => m.AlertPeople));

export const SEV_TEXT: Record<Alert["severity"], string> = { crit: "Urgent", warn: "Check soon", info: "For your information" };

export function useAlerts(accountId: string | undefined, staff: boolean) {
  return useLoad(() => (staff && !accountId ? Promise.resolve({ alerts: [] as Alert[] }) : api.alerts(staff ? accountId : undefined)), [accountId, staff], 60_000);
}

/**
 * The Shell's one alerts poller (app.tsx), shared by the tab badge, the farm home and the
 * Alerts page, so they don't each ask the server every minute. `reload` after an acknowledge.
 */
export const AlertsContext = createContext<ReturnType<typeof useAlerts> | null>(null);
export function useSharedAlerts() {
  const a = useContext(AlertsContext);
  if (!a) throw new Error("useSharedAlerts needs the Shell's AlertsContext");
  return a;
}

export function Alerts({ accountId, staff, sites, role, tab }: { accountId: string | undefined; staff: boolean; sites: Site[]; role: string; tab?: string | undefined }) {
  const canEdit = can(role, "edit-rules");
  const view = tab === "setup" ? "setup" : tab === "people" && canEdit ? "people" : "list";
  const forStaff = staff ? accountId : undefined;
  const tabs = (
    <div className="chips" role="tablist" aria-label="Alerts">
      <button className="chip" role="tab" aria-pressed={view === "list"} onClick={() => go({ name: "alerts" })}>
        Alerts
      </button>
      <button className="chip" role="tab" aria-pressed={view === "setup"} onClick={() => go({ name: "alerts", tab: "setup" })}>
        Set up
      </button>
      {canEdit && (
        <button className="chip" role="tab" aria-pressed={view === "people"} onClick={() => go({ name: "alerts", tab: "people" })}>
          Who's told
        </button>
      )}
    </div>
  );
  if (view === "setup")
    return (
      <Section title="Set up alerts" right={tabs}>
        <AlertRules accountId={forStaff} canEdit={canEdit} />
      </Section>
    );
  if (view === "people")
    return (
      <Section title="Who's told" right={tabs}>
        <AlertPeople accountId={forStaff} />
      </Section>
    );
  return <AlertList accountId={accountId} staff={staff} sites={sites} tabs={tabs} />;
}

function AlertList({ accountId, staff, sites, tabs }: { accountId: string | undefined; staff: boolean; sites: Site[]; tabs: ReactNode }) {
  const now = useNow();
  const alerts = useSharedAlerts();
  const [show, setShow] = useState<"open" | "all">("open");
  if (alerts.error && !alerts.data) return <Problem error={alerts.error} onRetry={alerts.reload} />;
  if (!alerts.data) return <Spinner />;
  const list = alerts.data.alerts.filter((a) => show === "all" || !a.acked);
  const siteName = new Map(sites.map((s) => [s.id, s.name]));
  const open = alerts.data.alerts.filter((a) => !a.acked).length;
  return (
    <Section title={`Alerts${open ? ` · ${open} open` : ""}`} right={tabs}>
      <div className="chips" role="group" aria-label="Show">
        <button className="chip" aria-pressed={show === "open"} onClick={() => setShow("open")}>
          Open
        </button>
        <button className="chip" aria-pressed={show === "all"} onClick={() => setShow("all")}>
          All this week
        </button>
      </div>
      <div className="card list">
        {!list.length && <div className="empty">{show === "open" ? "Nothing needs a look. Every device is reporting." : "No alerts this week."}</div>}
        {list.map((a) => (
          <AlertRow key={a.id} a={a} now={now} site={siteName.get(a.siteId)} accountId={staff ? accountId : undefined} onAcked={alerts.reload} />
        ))}
      </div>
      <p className="muted small" style={{ margin: 0 }}>
        Alerts come from what the devices report and the alerts set up under Set up. Turn on notifications on the Me page to get them on this phone or computer.
      </p>
    </Section>
  );
}

export function AlertRow({ a, now, site, accountId, onAcked, compact }: { a: Alert; now: number; site?: string | undefined; accountId: string | undefined; onAcked: () => void; compact?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <div className={`alert ${a.acked ? "ack" : ""}`}>
      <span className={`sev ${a.severity}`} aria-label={SEV_TEXT[a.severity]}>
        {a.severity === "crit" ? "!" : a.severity === "warn" ? "!" : "i"}
      </span>
      <div style={{ minWidth: 0 }}>
        <h3>
          <button className="linkish" onClick={() => go({ name: "device", eui: a.devEui })}>
            {a.title}
          </button>
        </h3>
        {!compact && <p>{a.detail}</p>}
        <p className="small">
          {site ? `${site} · ` : ""}
          {a.ongoing ? `since ${clock(a.at, now)}` : ago(a.at, now)}
          {a.acked ? ` · acknowledged by ${a.acked.by} ${ago(a.acked.at, now)}` : ""}
        </p>
        {error && <div className="error small">{error}</div>}
      </div>
      {!a.acked && (
        <button
          className="btn"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError("");
            try {
              await api.ackAlert(a.id, accountId);
              onAcked();
            } catch (e) {
              setError(e instanceof ApiError ? e.message : "Couldn't save.");
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "…" : "Acknowledge"}
        </button>
      )}
    </div>
  );
}
